import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import PageLoader from '../../../../components/loader/PageLoader'
import Pagination from '../../../../components/pagination/Pagination'
import SecretModal from './SecretModal'
import {
  getWebhookEndpoint,
  saveWebhookEndpoint,
  rotateWebhookSecret,
  deleteWebhookEndpoint,
  sendTestWebhook,
  getWebhookDeliveries,
  replayWebhookDelivery,
} from '../../../../utils/WebhookApi'

// Delivery state -> chip class. `success`/`warning`/`error` are the generic
// colours in the span-Status mixin; shipment status codes (OK, CCD, WC...)
// have their own entries there and are used directly.
const stateClass = (state: string) => {
  switch (state) {
    case 'delivered':
      return 'success'
    case 'pending':
    case 'delivering':
      return 'warning'
    case 'failed':
    case 'discarded':
      return 'error'
    default:
      return ''
  }
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '—'

function Webhooks() {
  const { notify } = useGlobalContext()

  const [loading, setLoading] = useState(true)
  const [endpoint, setEndpoint] = useState<any>(null)
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [rotating, setRotating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Held only long enough for the user to copy it. Never persisted.
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null)
  const [secretWasRotated, setSecretWasRotated] = useState(false)

  const [deliveries, setDeliveries] = useState<any>({ docs: [] })
  const [deliveriesLoading, setDeliveriesLoading] = useState(true)
  const [stateFilter, setStateFilter] = useState('')
  const [replayingId, setReplayingId] = useState('')

  function fetchEndpoint(showLoader = true) {
    if (showLoader) setLoading(true)
    getWebhookEndpoint()
      .then((result: any) => {
        if (result.status === 200) {
          setEndpoint(result.data)
          setUrl(result.data?.url || '')
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  function fetchDeliveries(page: number, pageSize: number, state?: string) {
    setDeliveriesLoading(true)
    getWebhookDeliveries(page, pageSize, state)
      .then((result: any) => {
        if (result.status === 200) {
          setDeliveries(result.data)
        } else {
          notify('error', result.message)
        }
        setDeliveriesLoading(false)
      })
      .catch(() => setDeliveriesLoading(false))
  }

  useEffect(() => {
    fetchEndpoint()
    fetchDeliveries(1, 10)
  }, [])

  function handleSave() {
    if (!url.trim()) {
      notify('error', 'Enter your endpoint URL')
      return
    }
    setSaving(true)
    setTestResult(null)
    saveWebhookEndpoint(url.trim())
      .then((result: any) => {
        if (result.status === 200 || result.status === 201) {
          notify('success', result.message)
          // A secret comes back only on first creation.
          if (result.data?.secret) {
            setRevealedSecret(result.data.secret)
            setSecretWasRotated(false)
          }
          setEndpoint(result.data)
          setUrl(result.data?.url || url.trim())
        } else {
          // The backend rejects non-HTTPS, private addresses and unresolvable
          // hosts with a specific reason — surface it verbatim.
          notify('error', result.message)
        }
        setSaving(false)
      })
      .catch(() => setSaving(false))
  }

  function handleTest() {
    setTesting(true)
    setTestResult(null)
    sendTestWebhook()
      .then((result: any) => {
        setTestResult(result.data ?? { ok: false, reason: result.message })
        if (result.data?.ok) notify('success', 'Test event delivered')
        else notify('error', result.data?.reason || result.message)
        setTesting(false)
      })
      .catch(() => setTesting(false))
  }

  function handleRotate() {
    setRotating(true)
    rotateWebhookSecret()
      .then((result: any) => {
        if (result.status === 200) {
          setRevealedSecret(result.data.secret)
          setSecretWasRotated(true)
          fetchEndpoint(false)
        } else {
          notify('error', result.message)
        }
        setRotating(false)
      })
      .catch(() => setRotating(false))
  }

  function handleDelete() {
    setDeleting(true)
    deleteWebhookEndpoint()
      .then((result: any) => {
        if (result.status === 200) {
          notify('success', 'Webhook endpoint deleted')
          setEndpoint(null)
          setUrl('')
          setTestResult(null)
          setConfirmDelete(false)
        } else {
          notify('error', result.message)
        }
        setDeleting(false)
      })
      .catch(() => setDeleting(false))
  }

  function handleReplay(eventId: string) {
    setReplayingId(eventId)
    replayWebhookDelivery(eventId)
      .then((result: any) => {
        if (result.status === 202) {
          notify('success', 'Delivery re-queued')
          fetchDeliveries(deliveries.page || 1, deliveries.limit || 10, stateFilter || undefined)
        } else {
          notify('error', result.message)
        }
        setReplayingId('')
      })
      .catch(() => setReplayingId(''))
  }

  const isDisabled = endpoint && !endpoint.active
  const urlChanged = endpoint && url.trim() !== endpoint.url

  if (loading) {
    return (
      <div className='home'>
        <PageLoader position='center' />
      </div>
    )
  }

  return (
    <div className='home'>
      {revealedSecret && (
        <SecretModal
          secret={revealedSecret}
          rotated={secretWasRotated}
          close={() => setRevealedSecret(null)}
        />
      )}

      <div className='home_tag flex justify-between items-center'>
        <h1>Webhooks</h1>
        {endpoint && (
          <span className={`chip ${endpoint.active ? 'success' : 'error'}`}>
            {endpoint.active ? 'Active' : 'Disabled'}
          </span>
        )}
      </div>

      <div className='webhook-page'>
        {/* Sandbox is a separate account entirely, not a mode — worth saying
            plainly so nobody expects live shipments to appear here. */}
        <div className='notice flex'>
          <Icon icon='mdi:flask-outline' />
          <div>
            <strong>This is your sandbox endpoint.</strong> It only ever
            receives events for shipments booked with your sandbox
            credentials. Your live account is a separate account with its own
            endpoint and its own signing secret — nothing configured here can
            reach it, and a live shipment can never arrive here. Point this at
            your staging server and verify your signature handling before you
            go live.
          </div>
        </div>

        <div className='notice flex'>
          <Icon icon='mdi:information-outline' />
          <div>
            <strong>Delivery timing.</strong> Shipment status is checked four
            times a day (00:00, 09:00, 14:00 and 18:00 WAT), and a webhook goes
            out as soon as a change is detected — so expect up to ~6 hours
            between an event and the webhook. Cancellations are sent within a
            minute.
          </div>
        </div>

        {isDisabled && (
          <div className='notice error flex'>
            <Icon icon='mdi:alert-circle-outline' />
            <div>
              <strong>This endpoint has been disabled.</strong>{' '}
              {endpoint.disabledReason ||
                'It failed too many deliveries in a row.'}{' '}
              Save your URL again to re-enable it and reset the failure count.
            </div>
          </div>
        )}

        {/* ---- Endpoint configuration ---- */}
        <div className='panel'>
          <h2>Endpoint</h2>
          <p className='hint'>
            We POST a signed JSON payload here whenever one of your shipments
            changes status. Must be HTTPS on port 443 and publicly reachable.
          </p>

          <div className='field'>
            <label>Endpoint URL</label>
            <input
              type='url'
              value={url}
              placeholder='https://hooks.yourcompany.com/qc-express'
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>

          {saving ? (
            <FormLoader position='center' />
          ) : (
            <div className='actions flex flex-wrap'>
              <button onClick={handleSave} disabled={!url.trim()}>
                {endpoint ? (urlChanged ? 'Save changes' : 'Re-save') : 'Save endpoint'}
              </button>
              {endpoint && (
                <>
                  <button
                    className='ghost'
                    onClick={handleTest}
                    disabled={testing || !endpoint.active}
                  >
                    {testing ? 'Sending…' : 'Send test event'}
                  </button>
                  <button
                    className='ghost'
                    onClick={handleRotate}
                    disabled={rotating}
                  >
                    {rotating ? 'Rotating…' : 'Rotate secret'}
                  </button>
                  <button
                    className='danger'
                    onClick={() => setConfirmDelete(true)}
                    disabled={deleting}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          )}

          {confirmDelete && (
            <div className='confirm flex items-center justify-between'>
              <span>
                Delete this endpoint? Queued events will be discarded and you
                will stop receiving webhooks.
              </span>
              <div className='flex'>
                <button
                  className='ghost'
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </button>
                <button className='danger' onClick={handleDelete}>
                  {deleting ? 'Deleting…' : 'Yes, delete'}
                </button>
              </div>
            </div>
          )}

          {testResult && (
            <div className={`test-result ${testResult.ok ? 'ok' : 'bad'} flex`}>
              <Icon
                icon={testResult.ok ? 'mdi:check-circle-outline' : 'mdi:close-circle-outline'}
              />
              <div>
                {testResult.ok ? (
                  <>
                    Your endpoint responded{' '}
                    <strong>{testResult.responseStatus}</strong>. Signature
                    verification is up to your handler — check your logs to
                    confirm it passed.
                  </>
                ) : (
                  <>Test failed: {testResult.reason}</>
                )}
              </div>
            </div>
          )}

          {endpoint && (
            <div className='meta flex flex-wrap'>
              <div>
                <span>Created</span>
                <p>{formatDate(endpoint.createdAt)}</p>
              </div>
              <div>
                <span>Last success</span>
                <p>{formatDate(endpoint.lastSuccessAt)}</p>
              </div>
              <div>
                <span>Last failure</span>
                <p>{formatDate(endpoint.lastFailureAt)}</p>
              </div>
              <div>
                <span>Consecutive failures</span>
                <p
                  style={{
                    color: endpoint.consecutiveFailures > 0 ? '#d32f2f' : '',
                  }}
                >
                  {endpoint.consecutiveFailures}
                </p>
              </div>
              <div>
                <span>Secret rotated</span>
                <p>{formatDate(endpoint.secretLastRotatedAt)}</p>
              </div>
            </div>
          )}
        </div>

        {/* ---- Integration reference ---- */}
        <div className='panel'>
          <h2>Verifying the signature</h2>
          <p className='hint'>
            Every request carries <code>X-QC-Signature</code> in the form{' '}
            <code>t=&lt;unix seconds&gt;,v1=&lt;hex&gt;</code>, where the hex is{' '}
            <code>HMAC-SHA256(secret, "&lt;t&gt;.&lt;raw body&gt;")</code>. Sign
            the raw body bytes — re-serialising the parsed JSON will not match.
          </p>
          <pre>
{`const crypto = require('crypto')

function verify(rawBody, header, secret, tolerance = 300) {
  const parts = Object.fromEntries(
    header.split(',').map((kv) => kv.split('=')),
  )
  const t = Number(parts.t)
  if (!t || !parts.v1) return false
  if (Math.abs(Date.now() / 1000 - t) > tolerance) return false

  const expected = crypto
    .createHmac('sha256', secret)
    .update(\`\${t}.\${rawBody}\`)
    .digest('hex')

  const a = Buffer.from(expected, 'hex')
  const b = Buffer.from(parts.v1, 'hex')
  return a.length === b.length && crypto.timingSafeEqual(a, b)
}`}
          </pre>
          <p className='hint'>
            Return any 2xx within 10 seconds — do your processing
            asynchronously. Failed deliveries retry six times over ~31 hours, so
            make your handler idempotent using the <code>X-QC-Event-Id</code>{' '}
            header. Events are not guaranteed to arrive in order; compare{' '}
            <code>occurredAt</code> against what you last recorded.
          </p>
        </div>
      </div>

      {/* ---- Delivery log ---- */}
      <div className='home_tag flex justify-between items-center'>
        <h1 style={{ fontSize: '20px' }}>Recent deliveries</h1>
        <div className='flex items-center select-wrapper'>
          <div className='flex items-center'>
            <p>State:</p>
            <select
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value)
                fetchDeliveries(1, 10, e.target.value || undefined)
              }}
            >
              <option value=''>All</option>
              <option value='delivered'>Delivered</option>
              <option value='pending'>Pending</option>
              <option value='failed'>Failed</option>
              <option value='discarded'>Discarded</option>
            </select>
          </div>
        </div>
      </div>

      <div className='home_tables'>
        <div className='left'>
          {deliveriesLoading ? (
            <FormLoader position='center' />
          ) : deliveries.docs?.length > 0 ? (
            <>
              <div className='mobile-table'>
                {deliveries.docs.map((item: any) => (
                  <div className='card' key={item._id}>
                    <div className='row flex justify-between'>
                      <h4>Status</h4>
                      <span className={item.status?.code}>
                        {item.status?.description}
                      </span>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Tracking</h4>
                      <p>{item.trackingId || '—'}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Reference</h4>
                      <p>{item.reference || '—'}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Delivery</h4>
                      <span className={stateClass(item.state)}>
                        {item.state}
                      </span>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Attempts</h4>
                      <p>{item.attempts}</p>
                    </div>
                    <div className='row flex justify-between'>
                      <h4>Occurred</h4>
                      <p>{formatDate(item.occurredAt)}</p>
                    </div>
                    {item.lastError && (
                      <div className='row flex justify-between'>
                        <h4>Last error</h4>
                        <p>{item.lastError}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <table className='desktop-table'>
                <thead>
                  <tr>
                    <th>Occurred</th>
                    <th>Tracking</th>
                    <th>Reference</th>
                    <th>Status</th>
                    <th>Delivery</th>
                    <th>Attempts</th>
                    <th>Response</th>
                    <th>Last error</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.docs.map((item: any) => (
                    <tr key={item._id}>
                      <td className='count'>{formatDate(item.occurredAt)}</td>
                      <td>{item.trackingId || '—'}</td>
                      <td>{item.reference || '—'}</td>
                      <td>
                        <span className={item.status?.code}>
                          {item.status?.description}
                        </span>
                      </td>
                      <td>
                        <span className={stateClass(item.state)}>
                          {item.state}
                        </span>
                      </td>
                      <td>{item.attempts}</td>
                      <td>{item.lastResponseStatus ?? '—'}</td>
                      <td
                        style={{
                          textTransform: 'none',
                          maxWidth: '220px',
                          fontSize: '12px',
                          color: '#d32f2f',
                        }}
                      >
                        {item.lastError || '—'}
                      </td>
                      <td>
                        {item.state !== 'delivered' && (
                          <span
                            onClick={() =>
                              replayingId ? null : handleReplay(item.eventId)
                            }
                          >
                            {replayingId === item.eventId
                              ? 'Sending…'
                              : 'Replay'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={deliveries.page}
                totalCount={deliveries.totalPages}
                pageSize={deliveries.limit}
                onPageChange={(page: number, pageSize: number) => {
                  fetchDeliveries(page, pageSize, stateFilter || undefined)
                }}
              />
            </>
          ) : (
            <div className='empty flex justify-center'>
              <div className='empty-cover'>
                <Icon icon='hugeicons:api' />
                <h1>
                  {endpoint
                    ? 'No deliveries yet'
                    : 'Add an endpoint to start receiving events'}
                </h1>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Webhooks
